/**
 * WebSocket v2 Subscription Manager
 *
 * This module implements multiplexed subscriptions over a single WebSocket connection.
 * It maintains the same external API as v1 but uses the new v2 protocol internally.
 */
import { getConfig } from './config';
import { getIdToken } from '../utils/utils';
import { AUTH_CHANGED_CODE, getFreshAuthToken, makeAuthExpiredError, normalizeAuthExpiredError, } from '../utils/auth-token-freshness';
import { decodeBase64Url, safeBtoa } from '../utils/core-platform';
import { docId } from './operations';
import { deriveDecline, dispatchDeclineEvent } from './decline';
import ReconnectingWebSocket from 'reconnecting-websocket';
// ============ Global State ============
// One connection per routing target (appId, or appId#room#<collection>/<roomId>)
const connections = new Map();
// Coalesce concurrent first-use/auth-preflight calls for the same routing target.
// React commonly mounts several subscriptions in one render; without this map,
// every call can pass the empty `connections` check, await auth, and create an
// orphan ReconnectingWebSocket before the first call publishes its connection.
// Keyed by the identity-bucket lease key (see connectionLeaseKey) so two distinct
// wallet tokens never share one in-flight socket even under a compact-hash collision.
const connectionFlights = new Map();
// A subscriber holds a routing-target lease from connection acquisition until
// its callback/subscription is registered. The last old-route unsubscribe must
// not close a connection already captured by a new route awaiting auth. Keyed by
// the same identity-bucket lease key as connectionFlights.
const connectionAcquisitions = new Map();
// Compact identity hashes remain useful bounded-size indexes, but collisions
// share a bucket instead of sharing authority. Each exact bearer keeps its own
// cache entry so collision isolation does not evict another principal's cache.
const responseCache = new Map();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
const TOKEN_REFRESH_BUFFER = 10 * 60 * 1000; // Refresh token 10 minutes before expiry
const TOKEN_CHECK_INTERVAL = 5 * 60 * 1000; // Check token freshness every 5 minutes
function getResponseCacheEntry(cacheKey, identityToken) {
    var _a;
    return (_a = responseCache.get(cacheKey)) === null || _a === void 0 ? void 0 : _a.find(entry => entry.identityToken === identityToken);
}
function setResponseCacheEntry(cacheKey, identityToken, data) {
    var _a;
    const now = Date.now();
    const bucket = (_a = responseCache.get(cacheKey)) !== null && _a !== void 0 ? _a : [];
    const reusableEntries = bucket.filter(entry => (entry.identityToken !== identityToken && now - entry.timestamp < CACHE_TTL));
    reusableEntries.push({ data, timestamp: now, identityToken });
    responseCache.set(cacheKey, reusableEntries);
}
// ============ WebSocket Config ============
const BASE_MIN_RECONNECT_DELAY_MS = 1000;
const MIN_RECONNECT_DELAY_JITTER_MS = 1000;
const MAX_RECONNECT_DELAY_MS = 300000;
const RECONNECT_DELAY_GROW_FACTOR = 1.8;
const MIN_BROWSER_RECONNECT_INTERVAL_MS = 5000;
const WS_AUTH_EXPIRED_CODE = 'auth_expired';
// React runs old-route effect cleanup before new-route effects. Keep an
// otherwise-idle socket for one short handoff window so the new route can
// cancel teardown and reuse it; genuinely idle sockets still close promptly.
const IDLE_CONNECTION_CLOSE_GRACE_MS = 5000;
const SUBSCRIPTION_CONFIRM_TIMEOUT_MS = 10000;
const WS_CONFIG = {
    // Keep retrying indefinitely so long outages recover without page refresh.
    maxRetries: Infinity,
    // Conservative starting delay with jitter spreads reconnects across clients.
    minReconnectionDelay: BASE_MIN_RECONNECT_DELAY_MS + Math.floor(Math.random() * MIN_RECONNECT_DELAY_JITTER_MS),
    // Stretch backoff to 5 minutes during prolonged outages to protect backend pools.
    maxReconnectionDelay: MAX_RECONNECT_DELAY_MS,
    // Moderate growth to avoid rapid retry bursts while still converging to max delay.
    reconnectionDelayGrowFactor: RECONNECT_DELAY_GROW_FACTOR,
    // Give handshakes more time before considering the attempt failed.
    connectionTimeout: 10000,
};
const WS_V2_PATH = '/ws/v2';
let browserReconnectHooksAttached = false;
let lastBrowserTriggeredReconnectAt = 0;
let reconnectInProgress = null;
// ============ Helper Functions ============
function generateSubscriptionId() {
    return `sub_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
}
function hashForKey(value) {
    let h = 5381;
    for (let i = 0; i < value.length; i++) {
        h = ((h << 5) + h + value.charCodeAt(i)) & 0x7fffffff;
    }
    return h.toString(36);
}
/**
 * SECURITY (H1): Response-cache keys must be scoped to the caller's identity, not
 * just the path/filter/shape. The compact `identity` prefix selects a bucket;
 * exact opaque bearer equality is required inside it before reuse. Together they
 * keep a private snapshot from being delivered to a different user with the same
 * options (shared process / SSR worker / browser login-switch; this cache has a
 * 5-minute TTL). Callers must opt in before entries are read or written; no-auth
 * subscriptions never populate an implicit anonymous cache bucket.
 */
function getCacheKey(path, prompt, shape, limit, cursor, filter, identity) {
    const normalizedPath = path.startsWith('/') ? path.slice(1) : path;
    const shapeKey = shape && Object.keys(shape).length > 0 ? JSON.stringify(shape) : '';
    const limitKey = limit !== undefined ? `:l${limit}` : '';
    const cursorKey = cursor ? `:c${hashForKey(cursor)}` : '';
    const filterKey = filter && Object.keys(filter).length > 0 ? `:f${hashForKey(JSON.stringify(filter))}` : '';
    return `${identity}|${normalizedPath}:${prompt || 'default'}:${shapeKey}${limitKey}${cursorKey}${filterKey}`;
}
/**
 * Derive an opaque identity string for the bearer material a subscription sends.
 * JWT payloads are deliberately not trusted here: cache isolation must happen
 * before the server has verified any claims.
 */
function principalFromIdToken(idToken) {
    return idToken ? `t${hashForKey(idToken)}` : null;
}
function livePrincipalFromIdToken(idToken) {
    if (!idToken)
        return null;
    try {
        const payload = JSON.parse(decodeBase64Url(idToken.split('.')[1]));
        const userId = payload['custom:userId'];
        if (typeof userId === 'string' && userId.length > 0)
            return userId;
        const walletAddress = payload['custom:walletAddress'];
        if (typeof walletAddress === 'string' && walletAddress.length > 0)
            return walletAddress;
        const subject = payload.sub;
        return typeof subject === 'string' && subject.length > 0 ? subject : null;
    }
    catch (_a) {
        return null;
    }
}
function authSnapshotFromToken(idToken) {
    return {
        tokenValue: idToken !== null && idToken !== void 0 ? idToken : null,
        principal: livePrincipalFromIdToken(idToken),
    };
}
function connectionMatchesAuthToken(connection, idToken) {
    const current = authSnapshotFromToken(idToken);
    if (!current.tokenValue) {
        return connection.authTokenValue === null && connection.authPrincipal === null;
    }
    if (connection.authTokenValue && connection.authTokenValue === current.tokenValue) {
        return true;
    }
    return !!connection.authPrincipal && !!current.principal && connection.authPrincipal === current.principal;
}
async function canSendAuthenticatedLiveIntent(connection, isServer) {
    const currentToken = await getIdToken(isServer);
    if (!currentToken)
        return false;
    if (!connection.authTokenValue && !connection.authPrincipal)
        return false;
    return connectionMatchesAuthToken(connection, currentToken);
}
async function getSubscriptionIdentity(effectiveAppId, isServer, overrides, anonymous) {
    // A read-only scoped instance is unauthenticated by construction. Key it as
    // anonymous and never read the ambient global session (a different app's).
    if (anonymous) {
        return { key: `${effectiveAppId}:anon`, cacheable: false, token: null };
    }
    // Per-subscription wallet override (server WalletClient.subscribe): key by
    // the wallet's own opaque token material, mirroring getReadPrincipalKey. Do
    // not trust decoded claims or the unverified _walletAddress hint.
    if (overrides === null || overrides === void 0 ? void 0 : overrides._getAuthHeaders) {
        try {
            const bearer = bearerFromAuthHeaders(await overrides._getAuthHeaders());
            const principal = principalFromIdToken(bearer);
            return principal
                ? { key: `${effectiveAppId}:o${principal}`, cacheable: true, token: bearer }
                : { key: `${effectiveAppId}:oanon`, cacheable: false, token: null };
        }
        catch (_a) {
            // Couldn't resolve the override identity — do not use response cache.
            return { key: `${effectiveAppId}:o-uncacheable-${safeBtoa(String(connectionEpoch++))}`, cacheable: false, token: null };
        }
    }
    const idToken = await getIdToken(isServer);
    const principal = principalFromIdToken(idToken);
    return principal
        ? { key: `${effectiveAppId}:${principal}`, cacheable: true, token: idToken !== null && idToken !== void 0 ? idToken : null }
        : { key: `${effectiveAppId}:anon`, cacheable: false, token: null };
}
/** Extract the bare bearer token from a `{ Authorization: 'Bearer <jwt>' }` map. */
function bearerFromAuthHeaders(headers) {
    const auth = (headers === null || headers === void 0 ? void 0 : headers.Authorization) || (headers === null || headers === void 0 ? void 0 : headers.authorization) || '';
    return auth.startsWith('Bearer ') ? auth.slice('Bearer '.length) : auth;
}
// Monotonic counter for unique-ish fallback identity keys (no Date.now in some envs).
let connectionEpoch = 0;
// isTokenExpired / makeAuthExpiredError / isAuthExpiredError /
// normalizeAuthExpiredError now live in utils/auth-token-freshness so the HTTP and
// WebSocket paths cannot drift apart. `classifyToken` replaces the old boolean,
// which collapsed "expiring soon" and "already expired" into one answer and read a
// token with no numeric `exp` as fresh.
function getTokenExpirationTime(token) {
    try {
        const payload = JSON.parse(decodeBase64Url(token.split('.')[1]));
        return payload.exp ? payload.exp * 1000 : null;
    }
    catch (_a) {
        return null;
    }
}
function failConnectionAuth(connection, error) {
    connection.authFailure = error;
    connection.pendingAuthToken = null;
    connection.pendingAuthSnapshot = null;
    connection.authTokenValue = null;
    connection.authPrincipal = null;
    connection.isConnecting = false;
    connection.isConnected = false;
    connection.isAuthenticating = false;
    for (const subscriptionId of Array.from(connection.pendingSubscriptions.keys())) {
        rejectPendingSubscriptionConfirmation(connection, subscriptionId, error);
    }
    for (const subscription of connection.subscriptions.values()) {
        notifyErrorCallbacks(subscription, error);
        rejectSubscriptionRefresh(subscription, error);
    }
}
function scheduleTokenRefresh(connection, isServer) {
    // Clear any existing timer
    if (connection.tokenRefreshTimer) {
        clearInterval(connection.tokenRefreshTimer);
        connection.tokenRefreshTimer = null;
    }
    // Periodic check: every TOKEN_CHECK_INTERVAL, check if the token needs refreshing.
    // This replaces the old single setTimeout approach which was unreliable for long
    // delays (browsers throttle/suspend timers in background tabs).
    connection.tokenRefreshTimer = setInterval(async () => {
        var _a, _b, _c, _d, _e;
        try {
            const currentToken = await getIdToken(isServer);
            if (!currentToken)
                return; // Unauthenticated, nothing to refresh
            const expirationTime = getTokenExpirationTime(currentToken);
            if (!expirationTime)
                return;
            const timeUntilExpiry = expirationTime - Date.now();
            if (timeUntilExpiry <= TOKEN_REFRESH_BUFFER) {
                console.info('[WS v2] Token expiring soon, proactively refreshing');
                // Refresh the token and store it. Do NOT reconnect — the server only
                // verifies JWT at $connect time, so the existing connection continues
                // working. The fresh token is picked up by urlProvider the next time
                // a reconnect naturally happens (network drop, keepalive timeout, etc.).
                //
                // Routed through the shared transaction. This used to read a refresh
                // token, call refreshSession, and write the result with nothing binding
                // it to the session it started from — so a timer that fired for S1 and
                // completed after a same-wallet re-login could overwrite S2's rotated
                // token, and the next refresh would trip the issuer's reuse detection
                // and revoke the whole family.
                try {
                    await getFreshAuthToken(isServer);
                    console.info('[WS v2] Token refreshed successfully, stored for next connection');
                }
                catch (refreshError) {
                    const definitive = (refreshError === null || refreshError === void 0 ? void 0 : refreshError.code) === AUTH_CHANGED_CODE
                        || !await getIdToken(isServer);
                    if (definitive) {
                        // The session is dead or belongs to someone else now. The shared
                        // helper has already cleared storage and notified auth-state
                        // listeners, so the UI stops showing a connection that is gone —
                        // this branch used to just stop the timer and leave the UI
                        // claiming to be connected.
                        console.warn('[WS v2] Session no longer usable, stopping periodic refresh');
                        if (connection.tokenRefreshTimer) {
                            clearInterval(connection.tokenRefreshTimer);
                            connection.tokenRefreshTimer = null;
                        }
                        return;
                    }
                    // Transient: keep the session and try again next interval.
                    // Status only — the raw axios error carries config.data (the refresh
                    // token) and must never hit the console / log sinks.
                    console.warn('[WS v2] Proactive token refresh failed, will retry next interval. status:', (_b = (_a = refreshError === null || refreshError === void 0 ? void 0 : refreshError.status) !== null && _a !== void 0 ? _a : refreshError === null || refreshError === void 0 ? void 0 : refreshError.code) !== null && _b !== void 0 ? _b : 'unknown');
                }
            }
        }
        catch (error) {
            console.error('[WS v2] Error in periodic token check. status:', (_e = (_d = (_c = error === null || error === void 0 ? void 0 : error.response) === null || _c === void 0 ? void 0 : _c.status) !== null && _d !== void 0 ? _d : error === null || error === void 0 ? void 0 : error.code) !== null && _e !== void 0 ? _e : 'unknown');
        }
    }, TOKEN_CHECK_INTERVAL);
}
/**
 * WS-local wrapper around the shared freshness helper.
 *
 * The shared helper owns expiry classification, the deduplicated
 * snapshot -> refresh -> commit transaction, and conditional clearing. A transient
 * failure must remain retryable; reconnect backoff bounds automatic attempts.
 */
async function getWsAuthToken(isServer) {
    var _a, _b;
    try {
        return await getFreshAuthToken(isServer);
    }
    catch (error) {
        // Status only — the raw axios error carries config.data (the refresh token)
        // and must never reach a console or log sink.
        console.error('[WS v2] Error acquiring auth token. status:', (_b = (_a = error === null || error === void 0 ? void 0 : error.status) !== null && _a !== void 0 ? _a : error === null || error === void 0 ? void 0 : error.code) !== null && _b !== void 0 ? _b : 'unknown');
        throw normalizeAuthExpiredError(error, 'Authentication expired and refresh failed');
    }
}
/**
 * The server refused the token sent on the auth frame: its error frame arrives
 * while the auth ack is still pending, just before the 1008 close. Local expiry
 * had vouched for that exact token (unexpired but invalid - e.g. a rotated
 * issuer key), so re-sending it would only be refused again. Force a genuinely
 * new one: a definitive issuer rejection clears the session inside the shared
 * helper (firing notifySessionCleared, which signs the UI out), a successful
 * refresh commits a new token - and the ordinary reconnect loop picks up either
 * outcome on its own. Fresh token: authenticates. No token: connects
 * anonymously and public reads replay instead of erroring forever.
 */
async function recoverFromServerAuthRejection(connection) {
    const rejected = connection.pendingAuthToken;
    if (!rejected)
        return;
    // Share an in-flight attempt and stop retrying a definitively refused token.
    // Transient failures may retry on the next socket reconnect, with its backoff.
    if (connection.serverRejectedAuthToken === rejected)
        return;
    connection.serverRejectedAuthToken = rejected;
    try {
        await getFreshAuthToken(connection.isServer, { force: true });
    }
    catch (error) {
        if ((error === null || error === void 0 ? void 0 : error.code) !== AUTH_CHANGED_CODE && (error === null || error === void 0 ? void 0 : error.status) !== 401 && (error === null || error === void 0 ? void 0 : error.status) !== 403
            && connection.serverRejectedAuthToken === rejected) {
            connection.serverRejectedAuthToken = null;
        }
        // The shared helper clears definitively rejected sessions and preserves
        // transient failures. Never reuse the failed result as a credential.
    }
}
async function getConnectionAuthToken(isServer, authTokenProvider, anonymous) {
    // A read-only scoped instance connects anonymously: no auth frame, and it
    // must never read (or refresh) the ambient global session - that session
    // belongs to a different app and would be rejected by this app's worker.
    if (anonymous) {
        return null;
    }
    if (!authTokenProvider) {
        return getWsAuthToken(isServer);
    }
    try {
        const token = await authTokenProvider();
        if (token)
            return token;
        throw makeAuthExpiredError('Authenticated websocket token provider returned no token');
    }
    catch (error) {
        throw normalizeAuthExpiredError(error, 'Authenticated websocket token provider failed');
    }
}
function hasDisconnectedActiveConnections() {
    for (const connection of connections.values()) {
        if (!connection.ws) {
            continue;
        }
        // Only nudge reconnection when socket is fully closed.
        // If ReconnectingWebSocket is already in CONNECTING/backoff state, don't interfere.
        if (connection.ws.readyState === WS_READY_STATE_CLOSED) {
            return true;
        }
    }
    return false;
}
function maybeReconnectFromBrowserSignal() {
    const now = Date.now();
    if (now - lastBrowserTriggeredReconnectAt < MIN_BROWSER_RECONNECT_INTERVAL_MS) {
        return;
    }
    if (!hasDisconnectedActiveConnections()) {
        return;
    }
    lastBrowserTriggeredReconnectAt = now;
    reconnectWithNewAuthV2().catch((error) => {
        console.error('[WS v2] Browser-triggered reconnect failed:', error);
    });
}
function attachBrowserReconnectHooksOnce() {
    if (browserReconnectHooksAttached) {
        return;
    }
    if (typeof window === 'undefined') {
        return;
    }
    browserReconnectHooksAttached = true;
    window.addEventListener('online', () => {
        maybeReconnectFromBrowserSignal();
    });
    if (typeof document !== 'undefined') {
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'visible') {
                maybeReconnectFromBrowserSignal();
            }
        });
    }
}
// ============ Connection Management ============
/**
 * Derive the stable `<collection>/<roomId>` room key from a routePath hint.
 * Returns null when the hint isn't a room path (fewer than 2 segments).
 */
function roomKeyFromRoutePath(routePath) {
    if (!routePath)
        return null;
    const segs = routePath.replace(/^\/+/, '').split('/').filter(Boolean);
    if (segs.length < 2)
        return null;
    return `${segs[0]}/${segs[1]}`;
}
function replaySubscriptions(connection) {
    for (const sub of connection.subscriptions.values()) {
        sub.lastData = undefined;
        if (!connection.pendingSubscriptions.has(sub.subscriptionId)) {
            registerDetachedReplayConfirmation(connection, sub);
        }
        sendSubscribeWithConfirmation(connection, sub);
    }
}
/**
 * Derive the stable identity-bucket lease key used to coalesce (singleflight)
 * and refcount (acquisition / idle-close) connection use. Ambient app/room
 * connections key by their routing target; wallet-scoped connections pin the
 * exact bearer so two distinct tokens never share a flight or a lease, even
 * when their compact `#id#<principalKey>` hashes collide.
 */
function connectionLeaseKey(appId, routePath, principalKey, principalToken, anonymous) {
    const roomKey = roomKeyFromRoutePath(routePath);
    // A read-only scoped instance is anonymous and must never share a socket with
    // the authenticated global path even when it targets the same appId.
    const appKey = anonymous ? `${appId}#ro` : appId;
    const base = roomKey ? `${appKey}#room#${roomKey}` : appKey;
    if (!principalKey)
        return base;
    const bucket = `${base}#id#${principalKey}`;
    return principalToken ? `${bucket}#tok#${principalToken}` : bucket;
}
function cancelIdleConnectionClose(connection) {
    if (!connection.idleCloseTimer)
        return;
    clearTimeout(connection.idleCloseTimer);
    connection.idleCloseTimer = null;
}
function closeConnectionNow(connection) {
    var _a;
    if (connections.get(connection.key) !== connection)
        return;
    if (!connection.ws)
        return;
    cancelIdleConnectionClose(connection);
    (_a = connection.authRetryWake) === null || _a === void 0 ? void 0 : _a.call(connection);
    if (connection.tokenRefreshTimer) {
        clearInterval(connection.tokenRefreshTimer);
        connection.tokenRefreshTimer = null;
    }
    const ws = connection.ws;
    connection.ws = null;
    connection.isConnecting = false;
    connection.isConnected = false;
    connection.isAuthenticating = false;
    const closeError = new Error('WebSocket connection closed');
    for (const subscriptionId of Array.from(connection.pendingSubscriptions.keys())) {
        rejectPendingSubscriptionConfirmation(connection, subscriptionId, closeError);
    }
    for (const subscription of connection.subscriptions.values()) {
        rejectSubscriptionRefresh(subscription, closeError);
    }
    connections.delete(connection.key);
    try {
        ws.close();
    }
    catch ( /* already closing */_b) { /* already closing */ }
}
function scheduleConnectionCloseIfIdle(connection) {
    var _a;
    if (connections.get(connection.key) !== connection)
        return;
    if (connection.subscriptions.size > 0)
        return;
    if (((_a = connectionAcquisitions.get(connection.acquisitionKey)) !== null && _a !== void 0 ? _a : 0) > 0)
        return;
    if (!connection.ws || connection.idleCloseTimer)
        return;
    connection.idleCloseTimer = setTimeout(() => {
        var _a;
        connection.idleCloseTimer = null;
        if (connection.subscriptions.size > 0)
            return;
        if (((_a = connectionAcquisitions.get(connection.acquisitionKey)) !== null && _a !== void 0 ? _a : 0) > 0)
            return;
        closeConnectionNow(connection);
    }, IDLE_CONNECTION_CLOSE_GRACE_MS);
}
/**
 * Acquire a connection under a routing-target lease. The lease is refcounted on
 * the identity-bucket key and held across the caller's auth preflight so a
 * concurrent old-route teardown cannot close a socket a new route is about to
 * reuse. The lease is released once the caller has registered its
 * subscription/callback.
 */
async function acquireConnection(appId, isServer, routePath, authTokenProvider, principalKey, principalToken, source) {
    var _a;
    const leaseKey = connectionLeaseKey(appId, routePath, principalKey, principalToken, source === null || source === void 0 ? void 0 : source.anonymous);
    connectionAcquisitions.set(leaseKey, ((_a = connectionAcquisitions.get(leaseKey)) !== null && _a !== void 0 ? _a : 0) + 1);
    let released = false;
    let releaseTimer = null;
    const release = (connection) => {
        var _a;
        if (released)
            return;
        released = true;
        if (releaseTimer) {
            clearTimeout(releaseTimer);
            releaseTimer = null;
        }
        const remaining = ((_a = connectionAcquisitions.get(leaseKey)) !== null && _a !== void 0 ? _a : 1) - 1;
        if (remaining > 0)
            connectionAcquisitions.set(leaseKey, remaining);
        else
            connectionAcquisitions.delete(leaseKey);
        if (connection)
            scheduleConnectionCloseIfIdle(connection);
    };
    try {
        const connection = await getOrCreateConnection(appId, isServer, routePath, authTokenProvider, principalKey, principalToken, source);
        const releaseConnection = () => release(connection);
        // Registration is synchronous after the caller's await. This fail-safe
        // releases on the next task even if an option getter/callback throws
        // before subscribeV2 reaches its explicit release path.
        releaseTimer = setTimeout(releaseConnection, 0);
        return { connection, release: releaseConnection };
    }
    catch (error) {
        release();
        throw error;
    }
}
async function getOrCreateConnection(appId, isServer, routePath, authTokenProvider, principalKey, principalToken, source) {
    // Singleflight: concurrent first-use of the same routing target shares one
    // in-flight connection rather than each creating an orphan socket. Keyed by
    // the identity-bucket lease key so distinct wallet tokens never coalesce.
    const leaseKey = connectionLeaseKey(appId, routePath, principalKey, principalToken, source === null || source === void 0 ? void 0 : source.anonymous);
    const activeFlight = connectionFlights.get(leaseKey);
    if (activeFlight)
        return activeFlight;
    const flight = getOrCreateConnectionOnce(appId, isServer, routePath, authTokenProvider, principalKey, principalToken, leaseKey, source);
    connectionFlights.set(leaseKey, flight);
    try {
        return await flight;
    }
    finally {
        if (connectionFlights.get(leaseKey) === flight) {
            connectionFlights.delete(leaseKey);
        }
    }
}
async function getOrCreateConnectionOnce(appId, isServer, routePath, authTokenProvider, principalKey, principalToken, leaseKey, source) {
    var _a, _b;
    attachBrowserReconnectHooksOnce();
    const anonymous = (source === null || source === void 0 ? void 0 : source.anonymous) === true;
    // A per-room subscription gets its OWN connection routed to the room DO; all
    // other subscriptions share the app-level connection (project DO). The worker
    // derives the room from the `?path=` we attach below and is the final routing
    // authority — `routePath` is only a hint.
    const roomKey = roomKeyFromRoutePath(routePath);
    // A wallet-scoped subscription (server WalletClient) gets its OWN connection
    // keyed by the wallet identity, so its WS authenticates as that wallet and
    // never shares a connection (or token) with any top-level server caller. A
    // read-only scoped (anonymous) instance is likewise isolated by an `#ro`
    // marker so it never shares the authenticated global socket on the same appId.
    const appKey = anonymous ? `${appId}#ro` : appId;
    const base = roomKey ? `${appKey}#room#${roomKey}` : appKey;
    const identityBucketKey = principalKey
        ? `${base}#id#${principalKey}`
        : base;
    let connKey = identityBucketKey;
    let connection;
    if (principalKey && principalToken) {
        // The 31-bit hash selects a small bucket only. Exact token equality is
        // authoritative, and a rare collision gets an independent map slot.
        for (const [candidateKey, candidate] of connections) {
            if ((candidateKey === identityBucketKey || candidateKey.startsWith(`${identityBucketKey}#collision#`)) &&
                candidate.connectionIdentityToken === principalToken) {
                connKey = candidateKey;
                connection = candidate;
                break;
            }
        }
        if (!connection && connections.has(connKey)) {
            let collisionIndex = 1;
            while (connections.has(`${identityBucketKey}#collision#${collisionIndex}`))
                collisionIndex += 1;
            connKey = `${identityBucketKey}#collision#${collisionIndex}`;
        }
    }
    else {
        connection = connections.get(connKey);
    }
    if (connection && connection.ws) {
        // We may reuse this connection; cancel any pending idle-close so it is
        // not torn down during the auth preflight below. The held acquisition
        // lease also guards this window, but cancelling here avoids a needless
        // grace-timer round-trip on the common reuse path.
        cancelIdleConnectionClose(connection);
        let currentAuthToken;
        try {
            currentAuthToken = await getConnectionAuthToken(isServer, authTokenProvider, anonymous);
        }
        catch (error) {
            const authError = normalizeAuthExpiredError(error, 'Authentication expired and refresh failed');
            failConnectionAuth(connection, authError);
            throw authError;
        }
        if (connection.authFailure) {
            // The preflight recovered, but failConnectionAuth invalidated this
            // socket's auth state. Authenticate again before replaying any reads.
            connection.authFailure = null;
            (_a = connection.authRetryWake) === null || _a === void 0 ? void 0 : _a.call(connection);
            connection.ws.reconnect();
            return connection;
        }
        if (connection.isConnected &&
            !connection.isAuthenticating &&
            !connectionMatchesAuthToken(connection, currentAuthToken)) {
            closeConnectionNow(connection);
        }
        else {
            connection.authFailure = null;
            return connection;
        }
        connection.authFailure = null;
    }
    let initialAuthToken = await getConnectionAuthToken(isServer, authTokenProvider, anonymous);
    // Create new connection
    connection = {
        ws: null,
        subscriptions: new Map(),
        pendingSubscriptions: new Map(),
        pendingUnsubscriptions: new Map(),
        pendingRequests: new Map(),
        isConnecting: false,
        isConnected: false,
        isAuthenticating: false,
        appId,
        key: connKey,
        acquisitionKey: leaseKey,
        routePath: roomKey ? routePath : undefined,
        authTokenProvider,
        connectionIdentityToken: principalToken !== null && principalToken !== void 0 ? principalToken : null,
        pendingAuthToken: null,
        pendingAuthSnapshot: null,
        authTokenValue: null,
        authPrincipal: null,
        socketGeneration: 0,
        tokenRefreshTimer: null,
        idleCloseTimer: null,
        consecutiveAuthFailures: 0,
        authFailure: null,
        source,
        anonymous,
        isServer,
        serverRejectedAuthToken: null,
    };
    // Resolve configuration before handing control to reconnecting-websocket.
    // Its 4.4.0 URL-provider promise has no rejection handler and a rejected
    // provider permanently holds _connectLock. Credential failures therefore
    // stay inside this bounded, cancellable acquisition loop.
    const wsApiUrl = (_b = source === null || source === void 0 ? void 0 : source.wsApiUrl) !== null && _b !== void 0 ? _b : (await getConfig()).wsApiUrl;
    const wsUrl = new URL(wsApiUrl);
    wsUrl.pathname = WS_V2_PATH;
    wsUrl.searchParams.append('appId', appId || (await getConfig()).appId);
    if (connection.routePath)
        wsUrl.searchParams.append('routePath', connection.routePath);
    connections.set(connKey, connection);
    const urlProvider = async () => {
        let retryDelay = WS_CONFIG.minReconnectionDelay;
        while (connections.get(connKey) === connection) {
            let authToken;
            try {
                authToken = initialAuthToken !== undefined
                    ? initialAuthToken
                    : await getConnectionAuthToken(isServer, connection.authTokenProvider, connection.anonymous);
                initialAuthToken = undefined;
            }
            catch (error) {
                if (connections.get(connKey) !== connection)
                    break;
                const authError = normalizeAuthExpiredError(error, 'Authentication expired and refresh failed');
                failConnectionAuth(connection, authError);
                // An error callback may synchronously tear down or replace this
                // connection. Do not install a retry after its cleanup completed.
                if (connections.get(connKey) !== connection)
                    break;
                await new Promise((resolve) => {
                    const wake = () => {
                        clearTimeout(timer);
                        if (connection.authRetryWake === wake)
                            connection.authRetryWake = undefined;
                        resolve();
                    };
                    const timer = setTimeout(wake, retryDelay);
                    connection.authRetryWake = wake;
                });
                retryDelay = Math.min(MAX_RECONNECT_DELAY_MS, retryDelay * RECONNECT_DELAY_GROW_FACTOR);
                continue;
            }
            if (connections.get(connKey) !== connection)
                break;
            connection.pendingAuthToken = authToken || null;
            connection.pendingAuthSnapshot = authSnapshotFromToken(authToken);
            connection.consecutiveAuthFailures = 0;
            connection.authFailure = null;
            return wsUrl.toString();
        }
        // Teardown has called close() on the wrapper, which prevents creation
        // of a native socket when this abandoned provider resolves.
        return wsUrl.toString();
    };
    // Create WebSocket connection
    connection.isConnecting = true;
    const ws = new ReconnectingWebSocket(urlProvider, [], Object.assign(Object.assign({}, WS_CONFIG), { debug: false }));
    connection.ws = ws;
    // Handle connection open
    ws.addEventListener('open', () => {
        var _a, _b;
        connection.socketGeneration += 1;
        connection.isConnecting = false;
        connection.isConnected = true;
        // NOTE: Do NOT reset consecutiveAuthFailures here. It is reset when a
        // fresh auth token is actually obtained or on an explicit auth change.
        // Schedule periodic token freshness checks. An anonymous (read-only
        // scoped) connection holds no token and must never poll or refresh the
        // ambient global session, so it is skipped.
        if (!connection.anonymous) {
            scheduleTokenRefresh(connection, isServer);
        }
        if (connection.pendingAuthToken) {
            connection.isAuthenticating = true;
            try {
                (_a = connection.ws) === null || _a === void 0 ? void 0 : _a.send(JSON.stringify({ type: 'auth', token: connection.pendingAuthToken }));
            }
            catch (error) {
                connection.isAuthenticating = false;
                connection.isConnected = false;
                console.error('[WS v2] Error sending auth message:', error);
                try {
                    (_b = connection.ws) === null || _b === void 0 ? void 0 : _b.close(1008, 'Authentication send failed');
                }
                catch (_c) {
                    // Already closed.
                }
            }
        }
        else {
            connection.isAuthenticating = false;
            connection.authTokenValue = null;
            connection.authPrincipal = null;
            replaySubscriptions(connection);
        }
    });
    // Handle incoming messages
    ws.addEventListener('message', (event) => {
        try {
            const message = JSON.parse(event.data);
            handleServerMessage(connection, message);
        }
        catch (error) {
            console.error('[WS v2] Error parsing message:', error);
        }
    });
    // Handle errors
    ws.addEventListener('error', (event) => {
        console.error('[WS v2] WebSocket error:', event);
        const wsError = new Error('WebSocket error');
        for (const subscriptionId of Array.from(connection.pendingSubscriptions.keys())) {
            rejectPendingSubscriptionConfirmation(connection, subscriptionId, wsError);
        }
        for (const subscription of connection.subscriptions.values()) {
            rejectSubscriptionRefresh(subscription, wsError);
        }
    });
    // Handle close
    ws.addEventListener('close', () => {
        connection.isConnected = false;
        connection.isAuthenticating = false;
        const disconnectError = new Error('WebSocket disconnected');
        for (const subscriptionId of Array.from(connection.pendingSubscriptions.keys())) {
            rejectPendingSubscriptionConfirmation(connection, subscriptionId, disconnectError);
        }
        if (connection.tokenRefreshTimer) {
            clearInterval(connection.tokenRefreshTimer);
            connection.tokenRefreshTimer = null;
        }
        // Reject all pending WS CRUD requests on disconnect (fail fast)
        for (const [id, pending] of connection.pendingRequests) {
            clearTimeout(pending.timer);
            pending.reject(disconnectError);
        }
        connection.pendingRequests.clear();
    });
    return connection;
}
function handleServerMessage(connection, message) {
    var _a, _b, _c, _d, _e, _f;
    switch (message.type) {
        case 'authenticated': {
            connection.isAuthenticating = false;
            connection.authTokenValue = (_b = (_a = connection.pendingAuthSnapshot) === null || _a === void 0 ? void 0 : _a.tokenValue) !== null && _b !== void 0 ? _b : null;
            connection.authPrincipal = (_d = (_c = connection.pendingAuthSnapshot) === null || _c === void 0 ? void 0 : _c.principal) !== null && _d !== void 0 ? _d : null;
            // Record server-advertised capabilities (v2 socket query transport).
            connection.capabilities = (_e = message.capabilities) !== null && _e !== void 0 ? _e : null;
            replaySubscriptions(connection);
            break;
        }
        case 'subscribed': {
            const subscription = connection.subscriptions.get(message.subscriptionId);
            if (subscription) {
                const refresh = subscription.refresh;
                // A data update can race ahead of subscribed during reconnect.
                // If we already received data for this subscription, treat subscribed
                // as an ack only and avoid regressing to an older snapshot.
                if (refresh) {
                    const hadBaseline = subscription.lastData !== undefined;
                    if (subscription.dataRevision === refresh.dataRevisionAtStart) {
                        if (subscription.cache) {
                            const cacheKey = getCacheKey(subscription.path, subscription.prompt, subscription.shape, subscription.limit, subscription.cursor, subscription.filter, subscription.identity);
                            setResponseCacheEntry(cacheKey, subscription.identityToken, message.data);
                        }
                        subscription.lastData = message.data;
                        subscription.dataRevision += 1;
                        if (hadBaseline) {
                            const callbacks = Array.from(refresh.callbacks)
                                .filter((callback) => subscription.callbacks.includes(callback));
                            notifyCallbackOptions(callbacks, message.data);
                        }
                        else {
                            // The refresh joined an original registration or a
                            // reconnect replay before its first baseline arrived.
                            // Every active callback needs that initial snapshot.
                            notifyCallbacks(subscription, message.data);
                        }
                    }
                    else if (refresh.callbacks.size > 0 && subscription.lastData !== undefined) {
                        // A newer authorized data frame arrived after this refresh
                        // began. Do not regress lastData to the older subscribed
                        // snapshot; use that newer frame for callbacks that joined
                        // after it was broadcast.
                        const callbacks = Array.from(refresh.callbacks)
                            .filter((callback) => subscription.callbacks.includes(callback));
                        notifyCallbackOptions(callbacks, subscription.lastData);
                    }
                    resolveSubscriptionRefresh(subscription);
                }
                else if (subscription.lastData === undefined) {
                    if (subscription.cache) {
                        const cacheKey = getCacheKey(subscription.path, subscription.prompt, subscription.shape, subscription.limit, subscription.cursor, subscription.filter, subscription.identity);
                        setResponseCacheEntry(cacheKey, subscription.identityToken, message.data);
                    }
                    subscription.lastData = message.data;
                    subscription.dataRevision += 1;
                    notifyCallbacks(subscription, message.data);
                }
            }
            // Resolve pending subscription promise
            const pending = connection.pendingSubscriptions.get(message.subscriptionId);
            if (pending) {
                resolvePendingSubscriptionConfirmation(connection, message.subscriptionId);
            }
            break;
        }
        case 'unsubscribed': {
            // Resolve pending unsubscription promise
            const pending = connection.pendingUnsubscriptions.get(message.subscriptionId);
            if (pending) {
                pending.resolve();
                connection.pendingUnsubscriptions.delete(message.subscriptionId);
            }
            break;
        }
        case 'data': {
            const subscription = connection.subscriptions.get(message.subscriptionId);
            if (subscription) {
                // Update cache
                if (subscription.cache) {
                    const cacheKey = getCacheKey(subscription.path, subscription.prompt, subscription.shape, subscription.limit, subscription.cursor, subscription.filter, subscription.identity);
                    setResponseCacheEntry(cacheKey, subscription.identityToken, message.data);
                }
                // Store last data
                subscription.lastData = message.data;
                subscription.dataRevision += 1;
                // Notify callbacks
                notifyCallbacks(subscription, message.data);
                // A data frame is itself a full, freshly authorized snapshot.
                // It satisfies every joined callback even if the duplicate
                // subscribed ACK was lost; resolving here prevents a false
                // refresh timeout ten seconds after the UI already rendered.
                if (subscription.refresh) {
                    resolveSubscriptionRefresh(subscription);
                }
            }
            // A data frame is a full, authorized snapshot and therefore also
            // confirms an initial registration when its `subscribed` ACK was
            // lost. Resolve the originating promise without duplicating state.
            resolvePendingSubscriptionConfirmation(connection, message.subscriptionId);
            break;
        }
        case 'response': {
            const pendingReq = connection.pendingRequests.get(message.requestId);
            if (pendingReq) {
                connection.pendingRequests.delete(message.requestId);
                clearTimeout(pendingReq.timer);
                if (message.status >= 400) {
                    pendingReq.reject(new Error(`Request failed with status ${message.status}`));
                }
                else {
                    pendingReq.resolve(message.data);
                }
            }
            break;
        }
        case 'setResponse': {
            const pendingSet = connection.pendingRequests.get(message.requestId);
            if (pendingSet) {
                connection.pendingRequests.delete(message.requestId);
                clearTimeout(pendingSet.timer);
                if (message.statusCode >= 400) {
                    pendingSet.reject(new Error(((_f = message.body) === null || _f === void 0 ? void 0 : _f.message) || `Set failed with status ${message.statusCode}`));
                }
                else {
                    pendingSet.resolve(message.body);
                }
            }
            break;
        }
        case 'error': {
            console.error('[WS v2] Server error:', message.code, message.message);
            // The auth frame was just refused: while the auth ack is pending the
            // server sends no other error frame, and this one is immediately
            // followed by its 1008 close. Kick the forced-refresh recovery - the
            // ordinary reconnect loop then re-auths with a fresh token or, once a
            // definitively dead session is cleared, connects anonymously so
            // public reads still replay. Ambient connections only: wallet-scoped
            // connections refresh through their own provider.
            if (connection.isAuthenticating && connection.pendingAuthToken && !connection.authTokenProvider && !connection.anonymous) {
                void recoverFromServerAuthRejection(connection);
            }
            // Build an Error that ALSO carries the stable `code` as a structured
            // field (not just string-interpolated into the message) so callers can
            // branch on it — e.g. `catch (e) { if (e.code === 'invariant_violation') }`
            // — symmetric with the HTTP path in utils/api.ts. requestId (CRUD) /
            // subscriptionId are attached for correlation with the decision log.
            const buildWsError = () => {
                const err = new Error(`${message.code}: ${message.message}`);
                err.code = message.code;
                if (message.requestId)
                    err.requestId = message.requestId;
                if (message.subscriptionId)
                    err.subscriptionId = message.subscriptionId;
                // A policy/invariant denial over the socket carries the same
                // structured decline as the HTTP path (live-intent acks, WS
                // writes). Mirror `.decline` and notify the browser widget.
                const declineBody = message.decline
                    ? { decline: message.decline }
                    : {
                        code: message.code,
                        message: message.message,
                        reason: message.reason,
                        invariant: message.invariant,
                        verdict: message.verdict,
                    };
                const decline = deriveDecline(declineBody);
                if (decline) {
                    err.decline = decline;
                    err.isBoundedDecline = true;
                    dispatchDeclineEvent(decline, connection.appId);
                }
                return err;
            };
            const wsError = buildWsError();
            // Handle CRUD request errors (requestId present)
            if (message.requestId) {
                const pendingReq = connection.pendingRequests.get(message.requestId);
                if (pendingReq) {
                    connection.pendingRequests.delete(message.requestId);
                    clearTimeout(pendingReq.timer);
                    pendingReq.reject(wsError);
                }
            }
            if (message.subscriptionId) {
                // Reject pending subscription if this is a subscription error
                const pending = connection.pendingSubscriptions.get(message.subscriptionId);
                if (pending) {
                    rejectPendingSubscriptionConfirmation(connection, message.subscriptionId, wsError);
                }
                // Notify error callbacks for this subscription
                const subscription = connection.subscriptions.get(message.subscriptionId);
                if (subscription) {
                    notifyErrorCallbacks(subscription, wsError);
                    rejectSubscriptionRefresh(subscription, wsError);
                }
            }
            break;
        }
    }
}
/**
 * Additively attach the bare `id` (leaf doc key) to a subscription row (Bug 1).
 * Rows carry `_id`/`pathId` = the full document path; this leaves those as-is and
 * adds `id` = the last path segment so `row.id` is the doc key apps expect. No-ops
 * for non-objects and rows that already define `id`.
 */
function withSubscriptionId(row) {
    var _a, _b, _c;
    if (!row || typeof row !== 'object' || Array.isArray(row))
        return row;
    if ('id' in row)
        return row;
    const source = (_c = (_b = (_a = row._id) !== null && _a !== void 0 ? _a : row.pathId) !== null && _b !== void 0 ? _b : row.relativePath) !== null && _c !== void 0 ? _c : row.absolutePath;
    if (typeof source !== 'string' || source.length === 0)
        return row;
    const leaf = docId(source);
    if (!leaf)
        return row;
    return Object.assign(Object.assign({}, row), { id: leaf });
}
/**
 * The `onData` payload follows the path: a collection delivers a bare array of
 * rows, a single-doc path delivers the document (or null). Attach the bare `id`
 * to each returned row in either case, additive (Bug 1).
 */
function addIdsToSubscriptionData(data) {
    if (Array.isArray(data))
        return data.map(withSubscriptionId);
    return withSubscriptionId(data);
}
function notifyCallbackOptions(callbacks, data) {
    var _a;
    // Attach the bare `id` (leaf doc key) to every delivered row (Bug 1).
    const decorated = addIdsToSubscriptionData(data);
    for (const callback of callbacks) {
        try {
            (_a = callback.onData) === null || _a === void 0 ? void 0 : _a.call(callback, decorated);
        }
        catch (error) {
            console.error('[WS v2] Error in subscription callback:', error);
        }
    }
}
function notifyCallbacks(subscription, data) {
    // Snapshot the callbacks array so that unsubscriptions during
    // notification don't cause callbacks to be skipped.
    notifyCallbackOptions(subscription.callbacks.slice(), data);
}
function notifyErrorCallback(callback, error) {
    let delivered = false;
    try {
        const onError = callback.onError;
        if (!onError)
            return false;
        onError(error);
        delivered = true;
    }
    catch (callbackError) {
        console.error('[WS v2] Error in subscription error callback:', callbackError);
    }
    return delivered;
}
function notifyErrorCallbacks(subscription, error) {
    let delivered = false;
    const callbacks = subscription.callbacks.slice();
    for (const callback of callbacks) {
        delivered = notifyErrorCallback(callback, error) || delivered;
    }
    if (delivered)
        error.__boundedDeliveredToOnError = true;
    return delivered;
}
// WebSocket readyState constants
const WS_READY_STATE_OPEN = 1;
const WS_READY_STATE_CLOSED = 3;
function sendSubscribe(connection, subscription) {
    if (!connection.ws || connection.ws.readyState !== WS_READY_STATE_OPEN) {
        return;
    }
    const message = {
        type: 'subscribe',
        subscriptionId: subscription.subscriptionId,
        path: subscription.path,
        filter: subscription.filter,
        sort: subscription.sort,
        prompt: subscription.prompt ? safeBtoa(subscription.prompt) : undefined,
        includeSubPaths: subscription.includeSubPaths,
        shape: subscription.shape && Object.keys(subscription.shape).length > 0
            ? subscription.shape
            : undefined,
        limit: subscription.limit,
        cursor: subscription.cursor,
    };
    try {
        connection.ws.send(JSON.stringify(message));
    }
    catch (error) {
        console.error('[WS v2] Error sending subscribe message:', error);
    }
}
function resolvePendingSubscriptionConfirmation(connection, subscriptionId) {
    const pending = connection.pendingSubscriptions.get(subscriptionId);
    if (!pending)
        return false;
    connection.pendingSubscriptions.delete(subscriptionId);
    if (pending.timer) {
        clearTimeout(pending.timer);
        pending.timer = null;
    }
    pending.resolve();
    return true;
}
function rejectPendingSubscriptionConfirmation(connection, subscriptionId, error) {
    const pending = connection.pendingSubscriptions.get(subscriptionId);
    if (!pending)
        return false;
    connection.pendingSubscriptions.delete(subscriptionId);
    if (pending.timer) {
        clearTimeout(pending.timer);
        pending.timer = null;
    }
    pending.reject(error);
    return true;
}
function armSubscriptionConfirmationTimeout(connection, subscription, pending) {
    pending.timer = setTimeout(() => {
        if (connection.pendingSubscriptions.get(subscription.subscriptionId) !== pending)
            return;
        pending.timer = null;
        const sameReadySocket = connection.ws === pending.socket &&
            connection.socketGeneration === pending.socketGeneration &&
            connection.authTokenValue === pending.authTokenValue &&
            connection.authPrincipal === pending.authPrincipal &&
            connection.isConnected &&
            !connection.isAuthenticating &&
            pending.socket.readyState === WS_READY_STATE_OPEN;
        const sameRegistration = connection.subscriptions.get(subscription.subscriptionId) === subscription;
        if (!pending.retrySent && sameReadySocket && sameRegistration) {
            pending.retrySent = true;
            // Arm the terminal timeout before sending: browser/test transports
            // may synchronously dispatch the acknowledgement from send().
            armSubscriptionConfirmationTimeout(connection, subscription, pending);
            sendSubscribe(connection, subscription);
            return;
        }
        rejectPendingSubscriptionConfirmation(connection, subscription.subscriptionId, new Error('Subscription timeout'));
    }, SUBSCRIPTION_CONFIRM_TIMEOUT_MS);
}
/**
 * Bind an existing confirmation promise to the exact socket session that is
 * about to carry its subscribe frame. Cold-start registrations create the
 * promise before the socket opens; replay uses this helper to move the timeout
 * from the connection wait onto the authenticated socket without allocating a
 * second promise or leaving the original timer behind.
 */
function sendSubscribeWithConfirmation(connection, subscription) {
    const pending = connection.pendingSubscriptions.get(subscription.subscriptionId);
    const socket = connection.ws;
    if (pending && socket && socket.readyState === WS_READY_STATE_OPEN) {
        if (pending.timer) {
            clearTimeout(pending.timer);
            pending.timer = null;
        }
        pending.socket = socket;
        pending.socketGeneration = connection.socketGeneration;
        pending.authTokenValue = connection.authTokenValue;
        pending.authPrincipal = connection.authPrincipal;
        // Arm before send: browser/test transports may synchronously dispatch
        // the subscribed/data/error response from send().
        armSubscriptionConfirmationTimeout(connection, subscription, pending);
    }
    sendSubscribe(connection, subscription);
}
/**
 * A reconnect replay has no caller awaiting subscribeV2 anymore, but it still
 * needs the same bounded confirmation/retry guarantee. Attach the rejection
 * handler immediately so this recovery record can never become an unhandled
 * promise. Structured server errors are delivered by handleServerMessage;
 * terminal local timeouts are surfaced here exactly once to active callbacks.
 */
function registerDetachedReplayConfirmation(connection, subscription) {
    const socket = connection.ws;
    if (!socket || connection.pendingSubscriptions.has(subscription.subscriptionId))
        return;
    let resolve;
    let reject;
    const promise = new Promise((resolvePromise, rejectPromise) => {
        resolve = resolvePromise;
        reject = rejectPromise;
    });
    const pending = {
        resolve,
        reject,
        timer: null,
        retrySent: false,
        socket,
        socketGeneration: connection.socketGeneration,
        authTokenValue: connection.authTokenValue,
        authPrincipal: connection.authPrincipal,
    };
    connection.pendingSubscriptions.set(subscription.subscriptionId, pending);
    void promise.catch((error) => {
        if (error.__boundedDeliveredToOnError)
            return;
        if (connection.subscriptions.get(subscription.subscriptionId) !== subscription)
            return;
        // Ordinary socket/auth-change close paths will replay again and did not
        // historically poison every established callback. Only a healthy socket
        // exhausting its bounded confirmation retry is a new terminal condition.
        if (error.message === 'Subscription timeout') {
            const delivered = notifyErrorCallbacks(subscription, error);
            if (!delivered) {
                console.warn('[WS v2] Replayed subscription confirmation timed out:', subscription.path);
            }
        }
    });
}
function resolveSubscriptionRefresh(subscription) {
    const refresh = subscription.refresh;
    if (!refresh)
        return;
    subscription.refresh = null;
    if (refresh.timer)
        clearTimeout(refresh.timer);
    refresh.callbacks.clear();
    refresh.resolve();
}
function rejectSubscriptionRefresh(subscription, error) {
    const refresh = subscription.refresh;
    if (!refresh)
        return;
    subscription.refresh = null;
    if (refresh.timer)
        clearTimeout(refresh.timer);
    refresh.callbacks.clear();
    refresh.reject(error);
}
/**
 * A callback joining an already-registered non-cache subscription cannot be
 * seeded from `lastData`: that snapshot may predate the callback's auth
 * preflight. Re-send the same subscription id so the server re-runs read auth
 * and returns a fresh baseline, without allocating another websocket or
 * replacing the logical client subscription.
 */
function requestFreshSubscriptionSnapshot(connection, subscription, callback) {
    let refresh = subscription.refresh;
    if (!refresh) {
        let resolve;
        let reject;
        const promise = new Promise((resolvePromise, rejectPromise) => {
            resolve = resolvePromise;
            reject = rejectPromise;
        });
        refresh = {
            promise,
            resolve,
            reject,
            timer: null,
            callbacks: new Set(),
            dataRevisionAtStart: subscription.dataRevision,
        };
        subscription.refresh = refresh;
        refresh.timer = setTimeout(() => {
            if (subscription.refresh !== refresh)
                return;
            rejectSubscriptionRefresh(subscription, new Error('Subscription timeout'));
        }, 10000);
    }
    // Register before sendSubscribe: test/browser transports may synchronously
    // dispatch the subscribed response from send().
    refresh.callbacks.add(callback);
    // Every newly joined callback gets an idempotent same-id resend when the
    // authenticated socket is ready. Do not suppress this because an initial
    // confirmation or an earlier refresh is pending: that pending frame may
    // have been lost or displaced, and sharing it is exactly how an entire
    // route remount can inherit one doomed timeout. When connecting or
    // authenticating, replaySubscriptions will send after auth completes.
    if (connection.isConnected && !connection.isAuthenticating) {
        sendSubscribe(connection, subscription);
    }
    return refresh.promise;
}
function sendUnsubscribe(connection, subscriptionId) {
    if (!connection.ws || connection.ws.readyState !== WS_READY_STATE_OPEN) {
        return;
    }
    const message = {
        type: 'unsubscribe',
        subscriptionId,
    };
    try {
        connection.ws.send(JSON.stringify(message));
    }
    catch (error) {
        console.error('[WS v2] Error sending unsubscribe message:', error);
    }
}
// ============ Public API ============
/**
 * Subscribe to data at a path using WebSocket v2.
 * Returns an unsubscribe function.
 */
/**
 * @param roomRoutePath INTERNAL ONLY. A `<sessionCollection>/<roomId>` room path
 *   used by `live.subscribeView` to route this subscription's connection to the
 *   per-room DO (where live view fan-out lives). It is NOT part of the public
 *   subscribe API — app code never passes routing; the worker remains the
 *   authority on the destination DO (it derives the room from the `?path=` we
 *   attach). This argument only tells the client which connection to multiplex
 *   the subscription onto, since a WS connection routes to a single DO.
 */
export async function subscribeV2(path, subscriptionOptions, roomRoutePath) {
    var _a, _b, _c, _d, _e, _f;
    const overrides = subscriptionOptions._overrides;
    // A scoped createClient() instance passes its own resolved config; the
    // default path reads the module-global init() config.
    const config = (_a = overrides === null || overrides === void 0 ? void 0 : overrides._config) !== null && _a !== void 0 ? _a : await getConfig();
    const normalizedPath = path.startsWith('/') ? path.slice(1) : path;
    // A read-only scoped instance connects anonymously to its own realtime
    // endpoint - never sharing a socket, token, or the ambient session with the
    // global (default) app.
    const anonymous = (overrides === null || overrides === void 0 ? void 0 : overrides._readOnly) === true;
    const source = (overrides === null || overrides === void 0 ? void 0 : overrides._config)
        ? { wsApiUrl: overrides._config.wsApiUrl, anonymous }
        : undefined;
    // Use explicit appId override if provided, otherwise fall back to config
    const effectiveAppId = subscriptionOptions.appId || config.appId;
    // H1: scope the response cache to the caller's identity so a private snapshot
    // cached for one principal is never delivered to a different principal who
    // subscribes with identical options (shared process / SSR / login-switch).
    // Per-subscription wallet override (server WalletClient.subscribe). When set,
    // the connection authenticates + caches under the wallet's identity instead of
    // any top-level server caller — so a `createWalletClient` caller can
    // subscribe through its explicit wallet session. A read-only scoped instance
    // is anonymous, so it never carries an auth-token provider.
    const authTokenProvider = (!anonymous && (overrides === null || overrides === void 0 ? void 0 : overrides._getAuthHeaders))
        ? async () => bearerFromAuthHeaders(await overrides._getAuthHeaders()) || null
        : undefined;
    const identityInfo = await getSubscriptionIdentity(effectiveAppId, config.isServer, overrides, anonymous);
    const identity = identityInfo.key;
    const identityToken = identityInfo.token;
    const responseCacheEnabled = subscriptionOptions.cache === true && identityInfo.cacheable;
    // Preserve the existing compact connection-key shape, but use the exact
    // token as a secondary in-memory equality check within that hash bucket.
    const principalKey = authTokenProvider ? identity : undefined;
    const principalToken = authTokenProvider
        ? (identityToken !== null && identityToken !== void 0 ? identityToken : identity)
        : undefined;
    const cacheKey = responseCacheEnabled
        ? getCacheKey(normalizedPath, subscriptionOptions.prompt, subscriptionOptions.shape, subscriptionOptions.limit, subscriptionOptions.cursor, subscriptionOptions.filter, identity)
        : null;
    // Acquire a connection for this routing target (room-scoped when a room route
    // is supplied by the live helper, else the app-level connection). The lease is
    // held across auth preflight and released once this call registers below, so a
    // concurrent old-route teardown cannot close a socket we are about to reuse.
    let connection;
    let releaseConnection;
    try {
        const acquisition = await acquireConnection(effectiveAppId, config.isServer, roomRoutePath, authTokenProvider, principalKey, principalToken, source);
        connection = acquisition.connection;
        releaseConnection = acquisition.release;
    }
    catch (error) {
        const err = error instanceof Error ? error : new Error(String(error));
        if (subscriptionOptions.onError) {
            subscriptionOptions.onError(err);
            return async () => { };
        }
        throw err;
    }
    const cachedEntry = cacheKey ? getResponseCacheEntry(cacheKey, identityToken) : undefined;
    // Check if we already have a subscription for this path+prompt+shape+limit+cursor+filter+sort
    let existingSubscription;
    let specKey = '';
    try {
        // Stringify THIS call's specs once; existing subscriptions carry their
        // precomputed specKey, so the scan is a string compare per entry
        // (previously each new subscribe re-stringified every existing
        // subscription's shape/filter/sort — O(N²) with many live hooks).
        specKey = JSON.stringify([
            (_b = subscriptionOptions.shape) !== null && _b !== void 0 ? _b : null,
            (_c = subscriptionOptions.filter) !== null && _c !== void 0 ? _c : null,
            (_d = subscriptionOptions.sort) !== null && _d !== void 0 ? _d : null,
        ]);
        for (const sub of connection.subscriptions.values()) {
            // H1: also require the same principal identity (compact hash AND exact
            // token) — a different logged-in user must not piggy-back on (and
            // inherit lastData from) another user's existing subscription, even
            // with identical path/prompt/shape/filter/sort.
            if (sub.identity === identity && sub.identityToken === identityToken && sub.path === normalizedPath && sub.prompt === subscriptionOptions.prompt && sub.specKey === specKey && sub.includeSubPaths === ((_e = subscriptionOptions.includeSubPaths) !== null && _e !== void 0 ? _e : false) && sub.limit === subscriptionOptions.limit && sub.cursor === subscriptionOptions.cursor) {
                existingSubscription = sub;
                break;
            }
        }
    }
    catch (error) {
        releaseConnection();
        throw error;
    }
    if (existingSubscription) {
        let replayData = undefined;
        let replayOnData;
        if (responseCacheEnabled) {
            // Read callback getters before mutating the shared subscription. If
            // a caller supplied a throwing getter, the acquisition fail-safe can
            // release cleanly without leaving a ghost callback behind.
            replayOnData = subscriptionOptions.onData;
            replayData = existingSubscription.lastData !== undefined
                ? existingSubscription.lastData
                : cachedEntry && Date.now() - cachedEntry.timestamp < CACHE_TTL
                    ? cachedEntry.data
                    : undefined;
        }
        if (responseCacheEnabled) {
            existingSubscription.cache = true;
        }
        // Add callback to existing subscription
        existingSubscription.callbacks.push(subscriptionOptions);
        let refreshPromise = null;
        if (responseCacheEnabled) {
            // Explicit, principal-bound cache opt-in may replay exactly one
            // snapshot. Prefer the active subscription's lastData, falling back
            // to the short-lived response cache while a reconnect is in flight.
            if (replayData !== undefined && replayOnData) {
                setTimeout(() => {
                    replayOnData === null || replayOnData === void 0 ? void 0 : replayOnData(addIdsToSubscriptionData(replayData));
                }, 0);
            }
        }
        else {
            // Default (non-cache) subscriptions require a fresh authorized
            // baseline for every newly joined callback. Reuse the same logical
            // subscription id and socket, but make the server re-run the read.
            refreshPromise = requestFreshSubscriptionSnapshot(connection, existingSubscription, subscriptionOptions);
        }
        releaseConnection();
        if (refreshPromise) {
            try {
                await refreshPromise;
            }
            catch (error) {
                const err = error instanceof Error ? error : new Error(String(error));
                let deliveredLocally = false;
                if (!err.__boundedDeliveredToOnError) {
                    deliveredLocally = notifyErrorCallback(subscriptionOptions, err);
                }
                if (!subscriptionOptions.onError) {
                    await removeCallbackFromSubscription(connection, existingSubscription.subscriptionId, subscriptionOptions);
                    throw err;
                }
                if (!err.__boundedDeliveredToOnError && !deliveredLocally) {
                    console.warn('[WS v2] Subscription refresh failed, keeping for reconnect recovery:', error);
                }
            }
        }
        return async () => {
            await removeCallbackFromSubscription(connection, existingSubscription.subscriptionId, subscriptionOptions);
        };
    }
    // Deliver cached data immediately for a new logical subscription, but only
    // after connection auth preflight has succeeded. An expired authenticated
    // session must receive an auth error, not a stale private snapshot followed
    // by a failed connect.
    if (cachedEntry && Date.now() - cachedEntry.timestamp < CACHE_TTL && subscriptionOptions.onData) {
        setTimeout(() => {
            var _a;
            (_a = subscriptionOptions.onData) === null || _a === void 0 ? void 0 : _a.call(subscriptionOptions, addIdsToSubscriptionData(cachedEntry.data));
        }, 0);
    }
    // Create new subscription
    const subscriptionId = generateSubscriptionId();
    let subscription;
    try {
        subscription = {
            subscriptionId,
            path: normalizedPath,
            filter: subscriptionOptions.filter,
            sort: subscriptionOptions.sort,
            prompt: subscriptionOptions.prompt,
            shape: subscriptionOptions.shape,
            limit: subscriptionOptions.limit,
            cursor: subscriptionOptions.cursor,
            includeSubPaths: (_f = subscriptionOptions.includeSubPaths) !== null && _f !== void 0 ? _f : false,
            callbacks: [subscriptionOptions],
            lastData: undefined,
            dataRevision: 0,
            refresh: null,
            cache: responseCacheEnabled,
            identity,
            identityToken,
            specKey,
        };
        connection.subscriptions.set(subscriptionId, subscription);
    }
    catch (error) {
        releaseConnection();
        throw error;
    }
    releaseConnection();
    // Register the confirmation before any possible synchronous send/ACK. This
    // includes the first subscription on a cold socket: replaySubscriptions()
    // will bind this same pending record to the authenticated socket and send it.
    const subscriptionPromise = new Promise((resolve, reject) => {
        const socket = connection.ws;
        if (!socket) {
            reject(new Error('WebSocket disconnected'));
            return;
        }
        const pending = {
            resolve,
            reject,
            timer: null,
            retrySent: false,
            socket,
            socketGeneration: connection.socketGeneration,
            authTokenValue: connection.authTokenValue,
            authPrincipal: connection.authPrincipal,
        };
        connection.pendingSubscriptions.set(subscriptionId, pending);
        // A fast auth refusal can open and close the socket before the first
        // caller finishes acquiring it. Keep this subscription for recovery,
        // but report the already observed disconnect without a timeout delay.
        if (pending.socketGeneration > 0 && socket.readyState === WS_READY_STATE_CLOSED) {
            rejectPendingSubscriptionConfirmation(connection, subscriptionId, new Error('WebSocket disconnected'));
            return;
        }
        // Bound a cold connection/auth wait as well. Once the socket becomes
        // ready, sendSubscribeWithConfirmation() replaces this timer with a
        // fresh per-socket confirmation window.
        armSubscriptionConfirmationTimeout(connection, subscription, pending);
    });
    if (connection.isConnected && !connection.isAuthenticating) {
        sendSubscribeWithConfirmation(connection, subscription);
    }
    try {
        await subscriptionPromise;
    }
    catch (error) {
        const err = error instanceof Error ? error : new Error(String(error));
        let deliveredLocally = false;
        if (!err.__boundedDeliveredToOnError) {
            // A local confirmation timeout belongs to the caller that
            // originated it. Joined callbacks may already have a viable
            // same-id refresh and must not be poisoned by that old attempt.
            deliveredLocally = notifyErrorCallback(subscriptionOptions, err);
        }
        if (!subscriptionOptions.onError) {
            rejectPendingSubscriptionConfirmation(connection, subscriptionId, err);
            // This registration may have acquired joined callbacks while
            // its confirmation was pending. Remove only the originating
            // callback; deleting the whole logical subscription would
            // orphan those remounted consumers and their refresh flight.
            await removeCallbackFromSubscription(connection, subscriptionId, subscriptionOptions);
            throw err;
        }
        if (!err.__boundedDeliveredToOnError && !deliveredLocally) {
            console.warn('[WS v2] Subscription confirmation failed, keeping for reconnect recovery:', error);
        }
    }
    // Return unsubscribe function
    return async () => {
        await removeCallbackFromSubscription(connection, subscriptionId, subscriptionOptions);
    };
}
async function removeCallbackFromSubscription(connection, subscriptionId, callback) {
    var _a;
    const subscription = connection.subscriptions.get(subscriptionId);
    if (!subscription) {
        return;
    }
    // Remove this callback
    subscription.callbacks = subscription.callbacks.filter(cb => cb !== callback);
    (_a = subscription.refresh) === null || _a === void 0 ? void 0 : _a.callbacks.delete(callback);
    // If there are still callbacks, don't unsubscribe
    if (subscription.callbacks.length > 0) {
        return;
    }
    // No more callbacks, unsubscribe from server
    connection.subscriptions.delete(subscriptionId);
    rejectPendingSubscriptionConfirmation(connection, subscriptionId, new Error('Subscription unsubscribed'));
    if (connection.isConnected && !connection.isAuthenticating) {
        // Create a promise to wait for unsubscription confirmation
        const unsubscribePromise = new Promise((resolve, reject) => {
            connection.pendingUnsubscriptions.set(subscriptionId, { resolve, reject });
            // Timeout after 5 seconds
            setTimeout(() => {
                if (connection.pendingUnsubscriptions.has(subscriptionId)) {
                    connection.pendingUnsubscriptions.delete(subscriptionId);
                    resolve(); // Don't throw on unsubscribe timeout
                }
            }, 5000);
        });
        sendUnsubscribe(connection, subscriptionId);
        await unsubscribePromise;
    }
    // If no more subscriptions, let the connection close after a short idle
    // grace so an immediately-following route remount can reclaim the socket.
    scheduleConnectionCloseIfIdle(connection);
}
/**
 * Close all v2 subscriptions.
 */
export async function closeAllSubscriptionsV2() {
    var _a;
    const closePromises = [];
    for (const [appId, connection] of connections) {
        cancelIdleConnectionClose(connection);
        const closeError = new Error('Subscriptions closed');
        for (const subscriptionId of Array.from(connection.pendingSubscriptions.keys())) {
            rejectPendingSubscriptionConfirmation(connection, subscriptionId, closeError);
        }
        for (const subscription of connection.subscriptions.values()) {
            rejectSubscriptionRefresh(subscription, closeError);
        }
        if (connection.tokenRefreshTimer) {
            clearInterval(connection.tokenRefreshTimer);
            connection.tokenRefreshTimer = null;
        }
        if (connection.ws) {
            const ws = connection.ws;
            connection.ws = null;
            connection.isConnected = false;
            if (ws.readyState !== WS_READY_STATE_CLOSED) {
                closePromises.push(new Promise((resolve) => {
                    ws.addEventListener('close', () => {
                        resolve();
                    });
                    ws.close();
                }));
            }
            else {
                // CLOSED may mean a URL/auth acquisition is still pending.
                // close() also disables the wrapper's future native socket.
                ws.close();
            }
        }
        connections.delete(appId);
        (_a = connection.authRetryWake) === null || _a === void 0 ? void 0 : _a.call(connection);
    }
    connectionAcquisitions.clear();
    await Promise.all(closePromises);
}
/**
 * Clear the v2 cache.
 */
export function clearCacheV2(path) {
    if (path) {
        const normalizedPath = path.startsWith('/') ? path.slice(1) : path;
        // Clear all cache entries for this path across ALL identities. Keys are
        // now `<identity>|<path>:...` (H1 principal-scoping), so match the path
        // portion AFTER the identity prefix — otherwise this is a silent no-op.
        for (const key of responseCache.keys()) {
            const sep = key.indexOf('|');
            const pathPart = sep >= 0 ? key.slice(sep + 1) : key;
            if (pathPart.startsWith(normalizedPath)) {
                responseCache.delete(key);
            }
        }
    }
    else {
        responseCache.clear();
    }
}
/**
 * Get cached data for a path (v2).
 */
export function getCachedDataV2(path, prompt) {
    const normalizedPath = path.startsWith('/') ? path.slice(1) : path;
    // H1: response caches are identity-scoped (`<identity>|<path>:...`) and
    // opt-in. Resolve the caller's identity from an active cache-enabled
    // subscription for this path. No matching sub → null (safe).
    let identity;
    let identityToken;
    outer: for (const connection of connections.values()) {
        for (const sub of connection.subscriptions.values()) {
            if (sub.cache && sub.path === normalizedPath && sub.prompt === prompt) {
                identity = sub.identity;
                identityToken = sub.identityToken;
                break outer;
            }
        }
    }
    if (!identity) {
        return null;
    }
    const cacheKey = getCacheKey(path, prompt, undefined, undefined, undefined, undefined, identity);
    const cachedEntry = getResponseCacheEntry(cacheKey, identityToken !== null && identityToken !== void 0 ? identityToken : null);
    if (cachedEntry && Date.now() - cachedEntry.timestamp < CACHE_TTL) {
        return cachedEntry.data;
    }
    return null;
}
/**
 * Reconnect all v2 WebSocket connections with fresh authentication.
 * Call this when the user's auth state changes (login/logout).
 *
 * This will:
 * 1. Close existing connections
 * 2. Re-establish with new auth token
 * 3. Re-subscribe to all active paths
 *
 * Note: Existing subscriptions will receive new initial data after reconnection.
 */
export async function reconnectWithNewAuthV2() {
    if (reconnectInProgress) {
        return reconnectInProgress;
    }
    reconnectInProgress = doReconnectWithNewAuth();
    try {
        await reconnectInProgress;
    }
    finally {
        reconnectInProgress = null;
    }
}
async function doReconnectWithNewAuth() {
    var _a;
    // SECURITY (H1): the logged-in identity is changing (login / logout / switch).
    // Wipe ALL principal-scoped read caches so the new identity can never observe
    // data cached for the previous one. Clear both the WS response cache and the
    // HTTP get() cache (the latter lives in operations.ts — imported dynamically
    // to avoid a static import cycle).
    responseCache.clear();
    // Drop in-memory per-subscription snapshots so they are not redelivered as the
    // previous identity's data after the socket reconnects under new auth.
    for (const connection of connections.values()) {
        for (const sub of connection.subscriptions.values()) {
            sub.lastData = undefined;
        }
    }
    try {
        const { clearReadCacheForAuthChange } = await import('./operations');
        clearReadCacheForAuthChange();
    }
    catch (error) {
        console.warn('[WS v2] Failed to clear HTTP read cache on auth change:', error);
    }
    try {
        const { reconnectRealtimeStoreWithNewAuth } = await import('./realtime-store');
        await reconnectRealtimeStoreWithNewAuth();
    }
    catch (error) {
        console.warn('[WS v2] Failed to reset legacy realtime store on auth change:', error);
    }
    for (const [appId, connection] of connections) {
        if (!connection.ws) {
            continue;
        }
        cancelIdleConnectionClose(connection);
        const reconnectError = new Error('Connection reconnecting due to auth change');
        for (const subscriptionId of Array.from(connection.pendingSubscriptions.keys())) {
            rejectPendingSubscriptionConfirmation(connection, subscriptionId, reconnectError);
        }
        for (const subscription of connection.subscriptions.values()) {
            rejectSubscriptionRefresh(subscription, reconnectError);
        }
        for (const [, pending] of connection.pendingUnsubscriptions) {
            pending.resolve();
        }
        connection.pendingUnsubscriptions.clear();
        // Reset auth failure counter — this is a proactive reconnect (login, token refresh)
        connection.consecutiveAuthFailures = 0;
        connection.authFailure = null;
        (_a = connection.authRetryWake) === null || _a === void 0 ? void 0 : _a.call(connection);
        // Close the WebSocket (this triggers reconnection in ReconnectingWebSocket)
        // We use reconnect() which will close and re-open with fresh URL (including new token)
        try {
            connection.ws.reconnect();
        }
        catch (error) {
            console.error('[WS v2] Error reconnecting:', error);
        }
    }
}
/**
 * Force close and reconnect all connections.
 * This is more aggressive than reconnectWithNewAuthV2() and ensures
 * a complete teardown and rebuild of connections.
 */
export async function forceReconnectV2() {
    var _a;
    const reconnectPromises = [];
    const isServer = (await getConfig()).isServer;
    for (const [appId, connection] of connections) {
        if (!connection.ws) {
            continue;
        }
        cancelIdleConnectionClose(connection);
        // Store subscriptions to restore
        const subscriptionsToRestore = Array.from(connection.subscriptions.values());
        // Close the connection
        connection.ws.close();
        connection.ws = null;
        connection.isConnected = false;
        connections.delete(appId);
        (_a = connection.authRetryWake) === null || _a === void 0 ? void 0 : _a.call(connection);
        // If there were active subscriptions, re-establish connection
        if (subscriptionsToRestore.length > 0) {
            const reconnectPromise = (async () => {
                // Get fresh connection
                const newConnection = await getOrCreateConnection(appId, isServer);
                // Re-register all subscriptions
                for (const sub of subscriptionsToRestore) {
                    newConnection.subscriptions.set(sub.subscriptionId, sub);
                }
                // Wait for connection to be ready, then re-subscribe
                // The 'open' handler in getOrCreateConnection will send subscribe messages
            })();
            reconnectPromises.push(reconnectPromise);
        }
    }
    await Promise.all(reconnectPromises);
}
// ============ WebSocket request helpers ============
const WS_REQUEST_TIMEOUT_MS = 30000;
function generateRequestId() {
    return `req_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
}
/**
 * Returns true if there is an active v2 WebSocket connection open.
 */
export function hasActiveConnection() {
    for (const connection of connections.values()) {
        if (connection.ws && connection.isConnected && !connection.isAuthenticating) {
            return true;
        }
    }
    return false;
}
/** WS frame byte ceiling for a runQuery batch. The server's raw message gate is
 *  65536 bytes and rejects an oversized frame pre-parse with an UNCORRELATED error
 *  (no requestId to fall back on), so the client keeps larger batches on HTTP
 *  (which accepts up to 1MB). Headroom left below the hard limit. */
const WS_RUNQUERY_MAX_FRAME_BYTES = 60000;
/**
 * v2 socket-when-warm transport for named/computed query polls (subscribeQuery /
 * runQuery). Runs `queries` over the ambient per-app authenticated socket instead
 * of HTTP `POST /queries`, but ONLY when that is observably safe and identity-correct:
 *   - a warm, OPEN, non-authenticating app socket already exists (never opens one);
 *   - the server advertised `capabilities.runQuery` on the authenticated ack;
 *   - the socket's authenticated principal matches the caller's CURRENT identity
 *     (else read rules would evaluate for the wrong user) - reuses the same gate as
 *     reliable live intents;
 *   - the serialized frame fits the WS message limit.
 * Returns the per-row array on a clean socket success; returns null - meaning
 * "fall back to HTTP" - for every miss/refusal (no socket, no capability, identity
 * skew, oversized batch, the server's own `{ useHttp: true }` deferral, or any
 * timeout / disconnect / error frame). The rows are byte-identical to POST /queries,
 * so the caller is transport-agnostic and never observes which transport won.
 */
export async function runNamedQueriesOverSocket(appId, queries, isServer) {
    var _a;
    const connection = connections.get(appId);
    if (!connection || !connection.ws || connection.ws.readyState !== WS_READY_STATE_OPEN || connection.isAuthenticating) {
        return null; // no warm socket -> HTTP (never open one just for queries)
    }
    if (!((_a = connection.capabilities) === null || _a === void 0 ? void 0 : _a.runQuery)) {
        return null; // server did not advertise the capability -> HTTP
    }
    if (!(await canSendAuthenticatedLiveIntent(connection, isServer).catch(() => false))) {
        return null; // identity skew / mid-refresh -> HTTP runs under the current token
    }
    const requestId = generateRequestId();
    const frame = JSON.stringify({ type: 'runQuery', requestId, queries });
    if (frame.length > WS_RUNQUERY_MAX_FRAME_BYTES) {
        return null; // oversized -> HTTP (see WS_RUNQUERY_MAX_FRAME_BYTES)
    }
    let data;
    try {
        data = await new Promise((resolve, reject) => {
            const timer = setTimeout(() => {
                connection.pendingRequests.delete(requestId);
                reject(new Error(`runQuery ack timed out after ${WS_REQUEST_TIMEOUT_MS}ms`));
            }, WS_REQUEST_TIMEOUT_MS);
            connection.pendingRequests.set(requestId, { resolve, reject, timer });
            try {
                connection.ws.send(frame);
            }
            catch (e) {
                connection.pendingRequests.delete(requestId);
                clearTimeout(timer);
                reject(e);
            }
        });
    }
    catch (_b) {
        return null; // timeout / disconnect / server error frame -> HTTP re-runs authoritatively
    }
    if ((data === null || data === void 0 ? void 0 : data.useHttp) === true)
        return null; // server deferred (onchain, cross-room, malformed)
    if (Array.isArray(data === null || data === void 0 ? void 0 : data.queries))
        return data.queries;
    return null; // unexpected shape -> HTTP
}
/**
 * Send a live-room intent over the EXISTING per-room socket (fire-and-forget).
 * Returns true if it was sent over an open connection, false if there is no
 * live socket for this room yet (caller falls back to HTTP). The worker is the
 * room authority — the connection is already routed to the room DO, so the
 * client never names a destination.
 */
export async function wsIntent(appId, roomRoutePath, intent, isServer) {
    const roomKey = roomKeyFromRoutePath(roomRoutePath);
    const connKey = roomKey ? `${appId}#room#${roomKey}` : appId;
    const connection = connections.get(connKey);
    if (!connection || !connection.ws || connection.ws.readyState !== WS_READY_STATE_OPEN || connection.isAuthenticating) {
        return false;
    }
    // Identity-aware send gate. The server attributes intents to the
    // CONNECTION's authenticated principal, so the only correct reasons to
    // refuse the socket are identity ones:
    //   - the socket is anonymous while the user is now authenticated, or
    //   - the current principal DIFFERS from the socket principal.
    // A mere token rotation for the SAME principal, or a transiently-null
    // token mid-refresh, must NOT reroute a 30Hz command stream to
    // per-command HTTP — that collapses delivery rate and unbounds the game's
    // ack lag (field-debugged: server 500+ commands behind).
    const currentToken = await getIdToken(isServer).catch(() => null);
    if (!currentToken) {
        // Mid-refresh: the socket principal still governs server-side.
        if (!connection.authTokenValue && !connection.authPrincipal)
            return false;
    }
    else if (!connection.authTokenValue && !connection.authPrincipal) {
        // Anonymous socket + authenticated user: do not attribute this user's
        // input to the anon principal. Reauth the socket; caller may fall back.
        void maybeReauthRoomConnection(connection, isServer);
        return false;
    }
    else if (!connectionMatchesAuthToken(connection, currentToken)) {
        // Different principal (identity switch): never send as the old user.
        void maybeReauthRoomConnection(connection, isServer);
        return false;
    }
    try {
        connection.ws.send(JSON.stringify({ type: 'intent', intent }));
        return true;
    }
    catch (_a) {
        return false;
    }
}
// Debounced background reauth: when the client's current token no longer
// matches the connection's authenticated identity (token rotation), refresh the
// socket auth ONCE rather than degrading every subsequent send.
const reauthInFlight = new WeakSet();
async function maybeReauthRoomConnection(connection, isServer) {
    try {
        if (reauthInFlight.has(connection))
            return;
        reauthInFlight.add(connection);
        try {
            await reconnectWithNewAuthV2();
        }
        finally {
            reauthInFlight.delete(connection);
        }
    }
    catch (_a) {
        // Best-effort: the next send retries; the server still enforces auth.
    }
}
/** True when an OPEN room socket exists for this room path. */
export function hasOpenRoomConnection(appId, roomRoutePath) {
    const roomKey = roomKeyFromRoutePath(roomRoutePath);
    const connKey = roomKey ? `${appId}#room#${roomKey}` : appId;
    const connection = connections.get(connKey);
    return !!(connection && connection.ws && connection.ws.readyState === WS_READY_STATE_OPEN);
}
/**
 * Send a live-room intent over the EXISTING per-room socket and AWAIT the server
 * ack — so a policy/auth denial (401/403/404/503) REJECTS instead of being
 * silently dropped (the fire-and-forget `wsIntent` discards request-less error
 * frames). Use for semantically important intents (join/ready/leave). Returns a
 * Promise that resolves on ack / rejects on error, or `undefined` if no live
 * socket exists yet (caller falls back to HTTP, which also surfaces errors).
 */
export async function wsIntentReliable(appId, roomRoutePath, intent, isServer) {
    const roomKey = roomKeyFromRoutePath(roomRoutePath);
    const connKey = roomKey ? `${appId}#room#${roomKey}` : appId;
    const connection = connections.get(connKey);
    if (!connection || !connection.ws || connection.ws.readyState !== WS_READY_STATE_OPEN || connection.isAuthenticating) {
        return undefined;
    }
    if (!(await canSendAuthenticatedLiveIntent(connection, isServer))) {
        return undefined;
    }
    const requestId = generateRequestId();
    return new Promise((resolve, reject) => {
        const timer = setTimeout(() => {
            connection.pendingRequests.delete(requestId);
            reject(new Error(`Live intent ack timed out after ${WS_REQUEST_TIMEOUT_MS}ms`));
        }, WS_REQUEST_TIMEOUT_MS);
        connection.pendingRequests.set(requestId, {
            resolve: (data) => resolve((data !== null && data !== void 0 ? data : { ok: true })),
            reject,
            timer,
        });
        try {
            connection.ws.send(JSON.stringify({ type: 'intent', requestId, intent }));
        }
        catch (e) {
            connection.pendingRequests.delete(requestId);
            clearTimeout(timer);
            reject(e);
        }
    });
}
